import { c as _c } from "react/compiler-runtime";
import { Box } from '@chakra-ui/react';
import { useEffect, useId, useState } from 'react';

// Mermaid sizes each node by laying out its label and shrinking the box to fit
// the measured text. Two things make that measurement disagree with what the
// browser finally paints — and the gap shows up as labels clipped on the side
// or bottom:
//   1. Font family mismatch. Mermaid measures with its default `"trebuchet ms"`
//      while this SVG is injected into a container that inherits `longform`
//      (Lato). Lato's glyphs are wider/taller than the box mermaid drew.
//   2. Late-loading web font. The render runs in an effect right after mount,
//      often before Lato has loaded, so mermaid measures against a narrower
//      fallback and the real glyphs overflow once Lato swaps in.
// Pin mermaid's font to the family the diagram actually renders in, and wait
// for that font to load before measuring, so the box and the glyphs agree.
const MERMAID_FONT = 'Lato, sans-serif';

// Lazy-load mermaid once (kept out of the main bundle); the promise is cached so
// every diagram shares one import. A failed import is not cached, so a later
// render can retry.
let mermaidPromise: Promise<(typeof import('mermaid'))['default']> | null = null;
const loadMermaid = () => {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(module => module.default, err => {
      mermaidPromise = null;
      throw err;
    });
  }
  return mermaidPromise;
};
type MermaidRenderResult = {
  svg: string;
} | {
  error: string;
} | null;

/**
 * Render `code` to an SVG string. Resolves null when cancelled before the
 * render starts; never rejects (errors come back as `{ error }`).
 */
const renderMermaidSvg = async (elementId: string, code: string, isCancelled: () => boolean): Promise<MermaidRenderResult> => {
  try {
    const mermaid = await loadMermaid();

    // Don't measure text until the render font is loaded, otherwise nodes
    // get sized against a fallback font and clip when Lato swaps in.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      await document.fonts.ready;
    }
    if (isCancelled()) {
      return null;
    }
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      fontFamily: MERMAID_FONT,
      themeVariables: {
        fontFamily: MERMAID_FONT
      },
      flowchart: {
        htmlLabels: true,
        useMaxWidth: true
      }
    });
    const {
      svg
    } = await mermaid.render(elementId, code);
    return {
      svg
    };
  } catch (err: any) {
    return {
      error: err?.message ?? 'Mermaid render error'
    };
  }
};
export const MermaidDiagram = t0 => {
  const $ = _c(14);
  const {
    code
  } = t0;
  const t1 = useId();
  let t2;
  if ($[0] !== t1) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /:/g;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t1.replace(t3, "");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const id = t2;
  const [svg, setSvg] = useState("");
  const [error, setError] = useState("");
  let t3;
  let t4;
  if ($[3] !== code || $[4] !== id) {
    t3 = () => {
      let cancelled = false;
      renderMermaidSvg(`mermaid-${id}`, code, () => cancelled).then(result => {
        if (cancelled || !result) {
          return;
        }
        if ("svg" in result) {
          setSvg(result.svg);
        } else {
          setError(result.error);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t4 = [code, id];
    $[3] = code;
    $[4] = id;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  if (error) {
    let t5;
    if ($[7] !== error) {
      t5 = <Box as="pre" borderRadius="md" borderWidth={1} color="red.500" fontSize="sm" p={2}>{error}</Box>;
      $[7] = error;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  let t5;
  if ($[9] !== svg) {
    t5 = {
      __html: svg
    };
    $[9] = svg;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      "& svg": {
        maxWidth: "100%",
        height: "auto",
        display: "block"
      },
      "& foreignObject": {
        overflow: "visible"
      },
      "& .nodeLabel, & .edgeLabel, & .label, & foreignObject div, & foreignObject span": {
        fontFamily: MERMAID_FONT,
        lineHeight: "normal"
      }
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5) {
    t7 = <Box dangerouslySetInnerHTML={t5} fontSize="16px" marginBottom={8} css={t6} />;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};